import { useTheme } from '@mui/material';
import TabsHeader from 'components/tabs-header';
import { useAccountState } from 'contexts/account/account-context';
import { Layers } from 'lucide-react';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { MarketId } from 'types/market-id.enum';
import { useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { OrderFeedParams } from 'views/order-feed/types/order-feed-params.type';

type Props = {
  intro?: React.ReactNode;
  actions?: React.ReactNode;
  utils?: React.ReactNode;
};

const OrderFeedTabsHeader: React.FC<Props> = ({ intro, actions, utils }) => {
  const intl = useIntl();
  const theme = useTheme();
  const params = useParams<OrderFeedParams>();
  const { account } = useAccountState();
  const {
    sitesQuery: { data: sites },
  } = useSitesState();
  const {
    notificationsQuery: { data: notifications },
  } = useOrderFeedState();

  const isOfd2Enabled = useMemo(() => {
    const site = sites?.find((site) => site.siteErpId === params.siteErpId);

    return account.company?.features.hasOrderFreeDelivery2 && site?.isOfd2Enabled ? true : false;
  }, [sites, params, account]);

  // Check if params are undefined or 'undefined'
  const IsParamsDefined = () => {
    return params.siteErpId && params.globalSiteId && params.siteErpId !== 'undefined' && params.globalSiteId !== 'undefined'; // params converted to string when directly going to Order History
  };

  return (
    <TabsHeader
      tabs={[
        {
          hasAccess: account.company?.features?.hasOrderFeed || account.company?.features?.hasOrderFreeDelivery,
          label: <FormattedMessage id="orderFeed.navigation.orderSupplyPlan" />,
          to: IsParamsDefined() // check for selected site
            ? isOfd2Enabled // we have params and the selected site has ofd 2
              ? `/order-feed/supply-plan/order-free-delivery/${params.siteErpId}/${params.globalSiteId}/orders`
              : `/order-feed/supply-plan/${params.siteErpId}/${params.globalSiteId}` // selected site is not ofd 2
            : '/order-feed/supply-plan', // no params
        },
        {
          hasAccess:
            account.company?.marketId === MarketId.Norway &&
            (account.company?.features?.hasOrderFeed || account.company?.features?.hasOrderRead),
          label: <FormattedMessage id="orderFeed.navigation.orderDistribution" />,
          notificationBadge: notifications?.activeOrderDistributionCount ?? 0,
          to: IsParamsDefined()
            ? `/order-feed/order-distribution/${params.siteErpId}/${params.globalSiteId}`
            : '/order-feed/order-distribution',
        },
        {
          hasAccess: account.company?.features?.hasOrderRead || account.company?.features?.hasOrderFeed,
          label: <FormattedMessage id="orderFeed.navigation.orderHistory" />,
          to: IsParamsDefined()
            ? `/order-feed/order-history/${params.siteErpId}/${params.globalSiteId}`
            : '/order-feed/order-history',
        },
        {
          hasAccess:
            (account.company?.features?.hasOrderFeed ||
              account.company?.features?.hasOrderFreeDelivery ||
              account.company?.features?.hasOrderRead) &&
            account.company?.marketId !== MarketId.Chile, // hide Purchase History for Chile
          label: <FormattedMessage id="orderFeed.navigation.purchaseHistory" />,
          to: IsParamsDefined()
            ? `/order-feed/purchase-history/${params.siteErpId}/${params.globalSiteId}`
            : '/order-feed/purchase-history',
        },
      ]}
      icon={<Layers size={25} color={theme.palette.common.white} />}
      intro={intro}
      actions={actions}
      utils={utils}
    />
  );
};

export default React.memo(OrderFeedTabsHeader);
